import React, { useState } from 'react';
import { 
  Modal, 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert,
  Divider,
  TextField
} from '@mui/material';
import { Block, CheckCircle, Warning } from '@mui/icons-material';
import api from '../../../services/api';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxWidth: '90vw'
};

const SuspendCampaignModal = ({ open, onClose, campaign, onSuccess, isSuspending }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState('');

  const handleAction = async () => {
    if (!campaign) return;
    setLoading(true);
    setError(null);
    
    try {
      await api.put(`/v1/api/campaigns/${campaign.id}/suspend`, {
        message: isSuspending ? reason : undefined
      });
      onSuccess(isSuspending ? 'suspended' : 'unsuspended');
      onClose();
    } catch (err) {
      console.error('Suspension error:', err);
      setError(err.response?.data?.message || 
              err.message || 
              `Failed to ${isSuspending ? 'suspend' : 'unsuspend'} campaign. Please try again later.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal 
      open={open} 
      onClose={onClose}
      aria-labelledby="suspend-modal-title"
    >
      <Box sx={style}>
        <Typography id="suspend-modal-title" variant="h6" component="h2" gutterBottom>
          {isSuspending ? 'Suspend' : 'Unsuspend'} Campaign
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Typography variant="subtitle1" gutterBottom>
          Campaign: <strong>{campaign?.title}</strong>
        </Typography>
        
        <Alert 
          severity="warning" 
          icon={<Warning fontSize="inherit" />} 
          sx={{ mb: 3 }}
        >
          {isSuspending 
            ? 'This will hide the campaign from public view and prevent further donations.'
            : 'This will make the campaign visible to the public again.'}
        </Alert>

        {isSuspending && (
          <TextField
            label="Reason for suspension"
            multiline
            rows={3}
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ mb: 3 }}
          />
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button 
            onClick={onClose}
            disabled={loading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={isSuspending ? 'error' : 'success'}
            startIcon={loading ? <CircularProgress size={20} /> : isSuspending ? <Block /> : <CheckCircle />}
            onClick={handleAction}
            disabled={loading || (isSuspending && !reason)}
          >
            {loading ? 'Processing...' : isSuspending ? 'Suspend' : 'Unsuspend'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SuspendCampaignModal;